import React, { useEffect, useMemo, useState } from 'react';
import { IUniversityData } from '../../../models/university/IUniversityData';
import styles from './university-grid-page-template.module.scss';
import UniversityCard from '../../molecules/university-card/university-card';
import UniversitiesListFilters from '../../molecules/universities-filter/universities-filter';
import { IToggleButton } from '../../../models/toggle-button/IToggleButton';
import { getEnumKeyByEnumValue } from '../../../utils/string-helpers/enum.helper';
import { CourseType } from '../../../constants/course-type';
import { ApplicationBranding } from '../../../models/settings';

export type Props = {
  universityData: IUniversityData[];
  uniListingsShowEntryRouteFilters: boolean;
  showEntryRoutePills: boolean;
  showIntegratedMastersLabel?: boolean;
  getUniversityLandingLink: (university: IUniversityData) => string;
  appBranding: ApplicationBranding;
  showICASlabel?: boolean;
};

export default function UniversityGridPage({
  universityData,
  getUniversityLandingLink,
  uniListingsShowEntryRouteFilters,
  showEntryRoutePills,
  showIntegratedMastersLabel,
  appBranding,
  showICASlabel,
}: Props) {
  const [universitiesToShow, setUniversitiesToShow] = useState<IUniversityData[]>(universityData);
  const [countryFilter, setCountryFilter] = useState<string>(``);
  const [entryRouteFilter, setEntryRouteFilter] = useState<string>(``);

  const renderData = useMemo(() => {
    const countryFilters: IToggleButton[] = getCountryFilters(
      universityData,
      setCountryFilter,
      appBranding,
    );

    // Set up entry route filters
    const entryRouteFilters = getEntryRouteFilters(
      universityData,
      showIntegratedMastersLabel,
      setEntryRouteFilter,
      appBranding,
      showICASlabel,
    );

    return { countryFilters, entryRouteFilters };
  }, [universityData, showIntegratedMastersLabel, appBranding, showICASlabel]);

  useEffect(() => {
    let filteredUniversities = universityData;

    if (countryFilter) {
      filteredUniversities = filteredUniversities.filter(
        (u) => u.country.isoAlpha2Code === countryFilter,
      );
    }

    if (entryRouteFilter) {
      filteredUniversities = filteredUniversities.filter(
        (u) => u.entryRoutes.indexOf(CourseType[entryRouteFilter]) > -1,
      );
    }
    setUniversitiesToShow(filteredUniversities);
  }, [countryFilter, entryRouteFilter, universityData]);

  return (
    <div className={`mb-md-4 mb-3 ${styles['university-grid-page']}`}>
      <div className={`${styles.filter_row} filters`}>
        <UniversitiesListFilters
          filterOptions={renderData.countryFilters}
          filterByLabel='Country:'
          heapIdentifier='country'
        />
        {uniListingsShowEntryRouteFilters && (
          <UniversitiesListFilters
            filterOptions={renderData.entryRouteFilters}
            filterByLabel='Entry Route:'
            heapIdentifier='entryRoute'
          />
        )}
      </div>
      <div className={`${styles.university_cards} row universities`}>
        {universitiesToShow?.length > 0 &&
          universitiesToShow.map((university) => (
            <div
              className={`${styles.uni_card} col-lg-3 col-md-4 col-sm-6 col-xs-12`}
              key={`${university.id}-${university.slug}`}
            >
              <UniversityCard
                university={university}
                key={university.id}
                getLink={getUniversityLandingLink}
                showEntryRoutePills={showEntryRoutePills}
                showIntegratedMastersLabel={showIntegratedMastersLabel}
                showICASlabel={showICASlabel}
              />
            </div>
          ))}
        {universitiesToShow?.length < 1 && (
          <div className='col-md-9 mt-4'>
            <h3>There are no universities that match the selected criteria.</h3>
          </div>
        )}
      </div>
    </div>
  );
}

const entryRouteSortOrder = [`All`, `Direct Entry`, `Pathway`, `A-levels`];

function getEntryRouteFilters(
  universityData: IUniversityData[],
  showIntegratedMastersLabel: boolean,
  setEntryRouteFilter: React.Dispatch<React.SetStateAction<string>>,
  appBranding: ApplicationBranding,
  showICASlabel: boolean,
) {
  var entryRouteFilters: IToggleButton[] = [];
  universityData.forEach((brand) => {
    brand.entryRoutes?.forEach((route) => {
      if (!entryRouteFilters.find((c) => c.value === getEnumKeyByEnumValue(CourseType, route))) {
        entryRouteFilters.push({
          value: getEnumKeyByEnumValue(CourseType, route),
          label:
            route === CourseType.Others && showIntegratedMastersLabel
              ? CourseType.IntegratedMastersProgram
              : route,
          group: `entryRoute`,
          handleOnToggle: () => setEntryRouteFilter(getEnumKeyByEnumValue(CourseType, route)),
          isChecked: false,
          inputColor: appBranding.primaryColor,
        });
      }
    });
  });

  if (showICASlabel === false && entryRouteFilters.find((e) => e.value === CourseType.ICAS)) {
    entryRouteFilters = entryRouteFilters.filter((e) => e.value !== CourseType.ICAS);
  }

  entryRouteFilters.sort(
    (a, b) => entryRouteSortOrder.indexOf(a.label) - entryRouteSortOrder.indexOf(b.label),
  );

  entryRouteFilters.unshift({
    value: ``,
    label: `All`,
    group: `entryRoute`,
    handleOnToggle: () => setEntryRouteFilter(``),
    isChecked: true,
    inputColor: appBranding.primaryColor,
  });

  return entryRouteFilters;
}

function getCountryFilters(
  universityData: IUniversityData[],
  setCountryFilter: React.Dispatch<React.SetStateAction<string>>,
  appBranding: ApplicationBranding,
) {
  const countryFilters: IToggleButton[] = [];
  universityData.forEach((brand) => {
    if (brand.country && !countryFilters.find((c) => c.value === brand.country?.isoAlpha2Code)) {
      countryFilters.push({
        value: brand.country.isoAlpha2Code,
        label: brand.country.shortCountryName,
        group: `country`,
        handleOnToggle: () => setCountryFilter(brand.country.isoAlpha2Code),
        isChecked: false,
        inputColor: appBranding.primaryColor,
      });
    }
  });

  countryFilters.sort((a, b) => a.label.localeCompare(b.label));

  countryFilters.unshift({
    value: ``,
    label: `All`,
    group: `country`,
    handleOnToggle: () => setCountryFilter(``),
    isChecked: true,
    inputColor: appBranding.primaryColor,
  }); // "All" should be first in the list. Here to prevent values like "Afghanistan" or "Albania" be first

  return countryFilters;
}
